import React, { useMemo } from 'react'
import { Drawer, Button, Table, Empty, Spin } from 'antd'
import { RiCloseLine } from 'react-icons/ri'
import { ArrowDownToLine, Users } from 'lucide-react'
import moment from 'moment'
import { translations } from '../../utils/constants'

const sortByDate = (a, b) => moment(a, 'DD/MM/YYYY').diff(moment(b, 'DD/MM/YYYY'))

// Groups the raw detail rows the same way the PDF report does:
// one block per MAC address + date, each with its own rows and totals.
const groupRecords = (records = []) => {
    const groups = {}

    for (const rec of records) {
        const mac = rec?.mac_address || '-'
        const date = rec?.created_date || '-'
        const key = `${mac}__${date}`

        if (!groups[key]) {
            groups[key] = {
                key,
                mac_address: mac,
                created_date: date,
                vehicle_name: rec?.vehicle_name || '-',
                organization_name: rec?.organization_name || '-',
                rows: [],
                enter: 0,
                exit: 0,
                remaining: 0
            }
        }

        const group = groups[key]
        group.rows.push(rec)
        group.enter += Number(rec?.enter ?? 0)
        group.exit += Number(rec?.exit ?? 0)
        group.remaining += Number(rec?.remaining ?? 0)
    }

    return Object.values(groups).sort((a, b) => {
        const byMac = a.mac_address.localeCompare(b.mac_address)
        return byMac !== 0 ? byMac : sortByDate(a.created_date, b.created_date)
    })
}

const PeopleCounterDetailDrawer = ({
    isVisible,
    onClose,
    language,
    records = [],
    loading = false,
    isSuperAdmin = false,
    periodLabel = null,
    onDownload,
    downloadLoading = false
}) => {
    const t = translations[language] || {}

    const groups = useMemo(() => groupRecords(records), [records])

    const grandTotals = useMemo(() => groups.reduce((acc, g) => ({
        enter: acc.enter + g.enter,
        exit: acc.exit + g.exit,
        remaining: acc.remaining + g.remaining
    }), { enter: 0, exit: 0, remaining: 0 }), [groups])

    const columns = [
        {
            title: t.serialNoTxt || 'No#',
            key: 'no',
            width: 70,
            align: 'center',
            render: (_, __, index) => index + 1
        },
        {
            title: t.timeTxt || 'Time',
            dataIndex: 'created_time',
            key: 'created_time',
            render: (text) => text || '-'
        },
        ...(isSuperAdmin ? [{
            title: t.organizations || 'Organization',
            dataIndex: 'organization_name',
            key: 'organization_name',
            render: (text) => <span className='capitalized-name'>{text || '-'}</span>
        }] : []),
        {
            title: t.txtEnterPassengers || 'Enter',
            dataIndex: 'enter',
            key: 'enter',
            align: 'center',
            render: (text) => <span className='rp-num rp-num-enter'>{text ?? 0}</span>
        },
        {
            title: t.txtExitPassengers || 'Exit',
            dataIndex: 'exit',
            key: 'exit',
            align: 'center',
            render: (text) => <span className='rp-num rp-num-exit'>{text ?? 0}</span>
        },
        {
            title: t.txtTotalPassengers || 'Total',
            dataIndex: 'remaining',
            key: 'remaining',
            align: 'center',
            render: (text) => <span className='rp-num rp-num-total'>{text ?? 0}</span>
        }
    ]

    const labelColSpan = isSuperAdmin ? 3 : 2

    const renderSummary = (group) => () => (
        <Table.Summary fixed>
            <Table.Summary.Row className='pc-detail-summary-row'>
                <Table.Summary.Cell index={0} colSpan={labelColSpan}>
                    <strong>{t.total || 'Total'}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align='center'>
                    <span className='rp-num rp-num-enter'>{group.enter}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align='center'>
                    <span className='rp-num rp-num-exit'>{group.exit}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align='center'>
                    <span className='rp-num rp-num-total'>{group.remaining}</span>
                </Table.Summary.Cell>
            </Table.Summary.Row>
        </Table.Summary>
    )

    return (
        <Drawer
            title={t.detailReportTitle || 'Passenger Detailed Report'}
            visible={isVisible}
            onClose={onClose}
            width={860}
            placement='right'
            closable={true}
            className='custom-drawer pc-detail-drawer'
            closeIcon={<span className='custom-close-icon'><RiCloseLine /></span>}
            style={{ zIndex: 1000 }}
            footer={
                <div className='pc-detail-footer'>
                    <Button className='report-clear-btn' onClick={onClose}>
                        {t.closeTxt || 'Close'}
                    </Button>
                    <Button
                        type='primary'
                        className='add-button'
                        onClick={onDownload}
                        loading={downloadLoading}
                        disabled={loading || !records?.length}
                    >
                        {t.downloadDetailReport || 'Download Detailed Report'}
                        <ArrowDownToLine style={{ height: '18px' }} className='icons' />
                    </Button>
                </div>
            }
        >
            {loading ? (
                <div className='pc-detail-loading'>
                    <Spin size='large' />
                </div>
            ) : !groups.length ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <div className='rp-empty'>
                            <p className='rp-empty-title'>{t.noReportData}</p>
                            <p className='rp-empty-hint'>{t.noReportDataHint}</p>
                        </div>
                    }
                />
            ) : (
                <div className='pc-detail-content'>
                    <div className='pc-detail-overview'>
                        <div className='pc-detail-overview-left'>
                            <span className='vl-total-chip'>
                                {t.totalRecord}: <strong>{records.length}</strong>
                            </span>
                            {periodLabel && <span className='vd-card-meta'>{periodLabel}</span>}
                        </div>
                        <div className='pc-detail-overview-totals'>
                            <span className='pc-detail-total-item'>
                                {t.txtEnterPassengers}
                                <span className='rp-num rp-num-enter'>{grandTotals.enter}</span>
                            </span>
                            <span className='pc-detail-total-item'>
                                {t.txtExitPassengers}
                                <span className='rp-num rp-num-exit'>{grandTotals.exit}</span>
                            </span>
                            <span className='pc-detail-total-item'>
                                {t.txtTotalPassengers}
                                <span className='rp-num rp-num-total'>{grandTotals.remaining}</span>
                            </span>
                        </div>
                    </div>

                    {groups.map((group) => (
                        <div className='vd-card pc-detail-group' key={group.key}>
                            <div className='vd-card-header'>
                                <div className='vl-list-title'>
                                    <h2 className='vd-card-title'>
                                        <Users size={16} className='rp-title-icon' />
                                        <span className='rp-vehicle capitalized-name'>{group.vehicle_name}</span>
                                    </h2>
                                    <span className='vl-plate'>{group.mac_address}</span>
                                </div>
                                <span className='vd-card-meta'>{group.created_date}</span>
                            </div>
                            <div className='vd-card-body'>
                                <Table
                                    className='vl-table pc-detail-table'
                                    size='small'
                                    rowKey={(r, i) => `${group.key}-${r?.created_time}-${i}`}
                                    dataSource={group.rows}
                                    columns={columns}
                                    pagination={false}
                                    summary={renderSummary(group)}
                                    scroll={{ x: 'max-content' }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Drawer>
    )
}

export default PeopleCounterDetailDrawer
